import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  Activity,
  ZoomIn,
  Languages,
  LogOut,
  Minus,
  Plus,
  Bug,
  FlaskConical,
  Pin,
  X,
  SlidersHorizontal,
  Bot,
  MessageSquare,
} from 'lucide-react';
import apiService from '../../services/api';
import * as tokenStorage from '../../utils/tokenStorage';
import {
  FONT_SCALE_MIN,
  FONT_SCALE_MAX,
  decreaseFontScale,
  getFontScale,
  getFontScaleLabel,
  increaseFontScale,
  isDebugLogPanelVisible,
  setDebugLogPanelVisible,
  isLegacyApplyEnabled,
  setLegacyApplyEnabled,
  isStickyScrollToCardEnabled,
  setStickyScrollToCardEnabled,
} from '../../utils/addInPreferences';
import { setRemoteDebugEnabled } from '../../utils/RemoteDebugLog';
import { webAppPathUrl } from '../../utils/webAppBaseUrl';
import { openExternalUrl } from '../../utils/openExternalUrl';
import ClientRequestPreferencesModal from './ClientRequestPreferencesModal';
import AgentAutoApplyPreferencesModal from './AgentAutoApplyPreferencesModal';
import QuickResponsePreferencesModal from './QuickResponsePreferencesModal';
import {
  getCustomQuickResponsesFromMetadata,
  QUICK_RESPONSE_OPTIONS,
} from '../../utils/quickResponses';
import { invalidateCustomQuickResponses } from '../../utils/customQuickResponsePrefs';
import { syncLanguageFromUser, setUiLanguage } from '../../i18n';
import {
  SUPPORTED_LANGUAGES,
  directionForLanguage,
  normalizeLanguage,
  type LanguageCode,
} from '../../i18n/config';
import './ProfileView.css';

interface UserInfo {
  id: string;
  email?: string;
  name?: string;
  full_name?: string;
  user_metadata?: {
    preferences?: {
      client_request_sources?: {
        module_a?: string[];
        module_b?: string[];
      };
      agent_auto_apply_enabled?: boolean;
      custom_quick_responses?: string[];
      language?: string;
    };
  };
}

interface ProfileViewProps {
  onClose: () => void;
  onSignOut: () => void;
  onPreferencesChange?: () => void;
}

const ADDIN_VERSION = '1.0.0';

function orgDisplayName(): string {
  const configured = (process.env.COURTYARD_ORG_NAME || '').trim();
  if (configured) return configured;
  return 'Courtyard';
}

function userInitial(email?: string, name?: string): string {
  const source = (name || email || '?').trim();
  return source.charAt(0).toUpperCase();
}

function parseStoredUser(): UserInfo | null {
  try {
    const raw = tokenStorage.getUser();
    return raw ? (JSON.parse(raw) as UserInfo) : null;
  } catch {
    return null;
  }
}

export const ProfileView: React.FC<ProfileViewProps> = ({ onClose, onSignOut, onPreferencesChange }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [user, setUser] = React.useState<UserInfo | null>(parseStoredUser);
  const [loading, setLoading] = React.useState(!parseStoredUser());
  const [fontScale, setFontScaleState] = React.useState(getFontScale);
  const [debugLogPanelVisible, setDebugLogPanelVisibleState] = React.useState(isDebugLogPanelVisible);
  const [legacyApplyEnabled, setLegacyApplyEnabledState] = React.useState(isLegacyApplyEnabled);
  const [stickyScrollEnabled, setStickyScrollEnabledState] = React.useState(isStickyScrollToCardEnabled);
  const [showClientRequestPrefs, setShowClientRequestPrefs] = React.useState(false);
  const [showAgentAutoApplyPrefs, setShowAgentAutoApplyPrefs] = React.useState(false);
  const [showQuickResponsePrefs, setShowQuickResponsePrefs] = React.useState(false);
  const [isSavingLanguage, setIsSavingLanguage] = React.useState(false);

  const loadUser = React.useCallback(async () => {
    const response = await apiService.getCurrentUser();
    if (response.success && response.data) {
      const userData: UserInfo = {
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        full_name: response.data.full_name,
        user_metadata: response.data.user_metadata,
      };
      setUser(userData);
      tokenStorage.setUser(JSON.stringify(userData));
      // Apply the user's saved UI language so the add-in matches the language
      // chosen in the dashboard Settings (shared server preference).
      syncLanguageFromUser(userData.user_metadata);
      return userData;
    }
    return null;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await loadUser();
      } catch (err) {
        console.warn('[ProfileView] Failed to load user:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  const clientRequestSources = user?.user_metadata?.preferences?.client_request_sources;
  const preferencesModuleA: string[] = clientRequestSources?.module_a ?? ['plain_text'];
  const preferencesModuleB: string[] = clientRequestSources?.module_b ?? ['their_side_redlines'];
  const moduleBSummary = preferencesModuleB
    .map((source) => t(`profile.sources.${source}`, { defaultValue: source }))
    .join(', ') || t('profile.none');
  const agentAutoApplyEnabled = user?.user_metadata?.preferences?.agent_auto_apply_enabled ?? false;
  const customQuickResponses = getCustomQuickResponsesFromMetadata(user?.user_metadata);
  const quickResponseSummary = customQuickResponses.length
    ? t('profile.quickResponseSummary', {
        custom: customQuickResponses.length,
        builtin: QUICK_RESPONSE_OPTIONS.length,
      })
    : t('profile.quickResponseBuiltinOnly', { builtin: QUICK_RESPONSE_OPTIONS.length });

  const notifyPreferencesChange = React.useCallback(() => {
    onPreferencesChange?.();
  }, [onPreferencesChange]);

  const handleFontScaleDecrease = () => {
    const next = decreaseFontScale();
    setFontScaleState(next);
    notifyPreferencesChange();
  };

  const handleFontScaleIncrease = () => {
    const next = increaseFontScale();
    setFontScaleState(next);
    notifyPreferencesChange();
  };

  const handleDebugLogPanelToggle = (next: boolean) => {
    setDebugLogPanelVisible(next);
    setRemoteDebugEnabled(next);
    setDebugLogPanelVisibleState(next);
    notifyPreferencesChange();
  };

  const handleLegacyApplyToggle = (next: boolean) => {
    setLegacyApplyEnabled(next);
    setLegacyApplyEnabledState(next);
    notifyPreferencesChange();
  };

  const handleStickyScrollToggle = (next: boolean) => {
    setStickyScrollToCardEnabled(next);
    setStickyScrollEnabledState(next);
    notifyPreferencesChange();
  };

  const handleLanguageChange = async (nextLanguage: LanguageCode) => {
    const normalized = normalizeLanguage(nextLanguage);
    if (normalized === normalizeLanguage(i18n.language)) return;
    setUiLanguage(normalized);
    setIsSavingLanguage(true);
    try {
      await apiService.updateLanguagePreference(normalized);
      await loadUser();
    } catch (err) {
      console.warn('[ProfileView] Failed to save language preference:', err);
    } finally {
      setIsSavingLanguage(false);
    }
  };

  const openExternal = (path: string) => {
    openExternalUrl(webAppPathUrl(path));
  };

  const subtitle = orgDisplayName();

  return (
    <div className="profile-view" dir={chromeDir}>
      <header className="profile-header">
        <div className="profile-header-start">
          <button
            type="button"
            className="profile-close-btn"
            onClick={onClose}
            aria-label={t('profile.closeAccount')}
          >
            <X size={18} />
          </button>
          <h1 className="profile-title">{t('profile.accountTitle')}</h1>
        </div>
        <img
          className="profile-org-logo"
          src="assets/Courtyard%20Logo%20-%20tight.png"
          alt="Courtyard"
        />
      </header>

      <section className="profile-user-card">
        <div className="profile-user-main" dir="ltr">
          <div className="profile-avatar" aria-hidden>
            {userInitial(user?.email, user?.full_name || user?.name)}
          </div>
          <div className="profile-user-text">
            {loading && !user ? (
              <span className="profile-user-loading">{t('profile.loadingAccount')}</span>
            ) : (
              <>
                <div className="profile-user-email" dir="ltr">{user?.email || '—'}</div>
                <div className="profile-user-subtitle" dir="ltr">{subtitle}</div>
              </>
            )}
          </div>
        </div>
      </section>

      <nav className="profile-menu" aria-label={t('profile.accountOptions')}>
        <button
          type="button"
          className="profile-menu-item"
          onClick={() => openExternal('/account/settings')}
        >
          <Settings className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.settings')}</span>
        </button>

        <button
          type="button"
          className="profile-menu-item"
          onClick={() => openExternal('/account/usage')}
        >
          <Activity className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.usage')}</span>
        </button>

        <button
          type="button"
          className="profile-menu-item profile-menu-item-preferences"
          onClick={() => setShowClientRequestPrefs(true)}
        >
          <SlidersHorizontal className="profile-menu-icon" size={18} />
          <span className="profile-menu-label-wrap">
            <span className="profile-menu-label">{t('profile.clientRequest')}</span>
            <span className="profile-menu-sublabel">{t('profile.moduleBSummary', { summary: moduleBSummary })}</span>
          </span>
          <span className="profile-menu-action">{t('profile.update')}</span>
        </button>

        <button
          type="button"
          className="profile-menu-item profile-menu-item-preferences"
          onClick={() => setShowQuickResponsePrefs(true)}
        >
          <MessageSquare className="profile-menu-icon" size={18} />
          <span className="profile-menu-label-wrap">
            <span className="profile-menu-label">{t('profile.quickResponse')}</span>
            <span className="profile-menu-sublabel">{quickResponseSummary}</span>
          </span>
          <span className="profile-menu-action">{t('profile.update')}</span>
        </button>

        <button
          type="button"
          className="profile-menu-item profile-menu-item-preferences"
          onClick={() => setShowAgentAutoApplyPrefs(true)}
        >
          <Bot className="profile-menu-icon" size={18} />
          <span className="profile-menu-label-wrap">
            <span className="profile-menu-label">{t('profile.agentAutoApply')}</span>
            <span className="profile-menu-sublabel">
              {agentAutoApplyEnabled ? t('profile.agentAutoApplyOn') : t('profile.agentAutoApplyOff')}
            </span>
          </span>
          <span className="profile-menu-action">{t('profile.update')}</span>
        </button>

        <div className="profile-menu-item profile-menu-item-static">
          <Pin className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Sticky</span>
          <label className="profile-toggle">
            <input
              type="checkbox"
              checked={stickyScrollEnabled}
              onChange={(e) => handleStickyScrollToggle(e.target.checked)}
              aria-label="When on, clicking a clause in the Word document scrolls to its card"
            />
            <span className="profile-toggle-track" aria-hidden />
          </label>
        </div>

        <div className="profile-menu-item profile-menu-item-static">
          <ZoomIn className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.screenResolution')}</span>
          <div className="profile-font-controls">
            <button
              type="button"
              className="profile-font-btn"
              onClick={handleFontScaleDecrease}
              disabled={fontScale <= FONT_SCALE_MIN}
              aria-label={t('profile.decreaseResolution')}
            >
              <Minus size={14} />
            </button>
            <span className="profile-font-label">{getFontScaleLabel(fontScale)}</span>
            <button
              type="button"
              className="profile-font-btn"
              onClick={handleFontScaleIncrease}
              disabled={fontScale >= FONT_SCALE_MAX}
              aria-label={t('profile.increaseResolution')}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="profile-menu-item profile-menu-item-static profile-menu-item-language">
          <Languages className="profile-menu-icon" size={18} />
          <span className="profile-menu-label-wrap">
            <span className="profile-menu-label">{t('profile.language')}</span>
            <span className="profile-menu-sublabel">{t('profile.languageDescription')}</span>
          </span>
          <select
            className="profile-language-select"
            value={normalizeLanguage(i18n.language)}
            onChange={(e) => handleLanguageChange(e.target.value as LanguageCode)}
            disabled={isSavingLanguage}
            aria-label={t('profile.language')}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code} dir={lang.dir}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>

        <div className="profile-menu-item profile-menu-item-static">
          <Bug className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.debugLogging')}</span>
          <label className="profile-toggle">
            <input
              type="checkbox"
              checked={debugLogPanelVisible}
              onChange={(e) => handleDebugLogPanelToggle(e.target.checked)}
              aria-label={t('profile.debugLoggingHint')}
            />
            <span className="profile-toggle-track" aria-hidden />
          </label>
        </div>

        <div className="profile-menu-item profile-menu-item-static">
          <FlaskConical className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.legacyApply')}</span>
          <label className="profile-toggle">
            <input
              type="checkbox"
              checked={legacyApplyEnabled}
              onChange={(e) => handleLegacyApplyToggle(e.target.checked)}
              aria-label={t('profile.legacyApplyHint')}
            />
            <span className="profile-toggle-track" aria-hidden />
          </label>
        </div>

        <button type="button" className="profile-menu-item profile-menu-item-signout" onClick={onSignOut}>
          <LogOut className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">{t('profile.signOut')}</span>
        </button>
      </nav>

      <footer className="profile-footer">
        <div className="profile-footer-meta">
          <span>{t('profile.footerVersion', { version: ADDIN_VERSION })}</span>
          <span>{t('profile.footerRights')}</span>
        </div>
      </footer>

      <ClientRequestPreferencesModal
        isOpen={showClientRequestPrefs}
        onClose={() => setShowClientRequestPrefs(false)}
        initialModuleA={preferencesModuleA}
        initialModuleB={preferencesModuleB}
        onSaved={async () => {
          await loadUser();
        }}
      />

      <AgentAutoApplyPreferencesModal
        isOpen={showAgentAutoApplyPrefs}
        onClose={() => setShowAgentAutoApplyPrefs(false)}
        initialEnabled={agentAutoApplyEnabled}
        onSaved={async () => {
          await loadUser();
          notifyPreferencesChange();
        }}
      />

      <QuickResponsePreferencesModal
        isOpen={showQuickResponsePrefs}
        onClose={() => setShowQuickResponsePrefs(false)}
        initialCustomLabels={customQuickResponses}
        onSaved={async () => {
          invalidateCustomQuickResponses();
          await loadUser();
        }}
      />
    </div>
  );
};
